import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  theme,
  Table,
  Tag,
  Button,
  Space,
  App,
  Spin,
  Descriptions,
  Typography,
  Collapse,
  Popconfirm,
  Alert,
  Card,
  Avatar,
  Modal,
  Timeline,
  Badge,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  LinkOutlined,
  ArrowLeftOutlined,
  UserOutlined,
  DisconnectOutlined,
  CloudDownloadOutlined,
  SyncOutlined,
  DownloadOutlined,
  ScanOutlined,
} from '@ant-design/icons';
import {
  KYCCaseListItem,
  KYCCasesListResponse,
  KYCStatus,
  KYCResult,
  UserDetailResponse,
  PostIdentDataResponse,
  MrzPipelineStepStatus,
  LlmVerificationStatus,
} from '@mygate/common';
import { kycService } from '@/services/kyc';
import { usersService } from '@/services/users';
import { useAuth } from '@/hooks/useAuth';
import { formatDateTime } from '@/utils/date';

const { Text } = Typography;
const { Panel } = Collapse;

export function KYCCases() {
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [userDetail, setUserDetail] = useState<UserDetailResponse | null>(null);
  const [kycData, setKycData] = useState<KYCCasesListResponse | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [postIdentData, setPostIdentData] = useState<PostIdentDataResponse | null>(null);
  const [postIdentModalVisible, setPostIdentModalVisible] = useState(false);
  const [fetchingPostIdentCases, setFetchingPostIdentCases] = useState<Set<string>>(new Set());
  const [extractingCases, setExtractingCases] = useState<Set<string>>(new Set());
  const [mrzExtractingCases, setMrzExtractingCases] = useState<Set<string>>(new Set());
  const { staff } = useAuth();

  useEffect(() => {
    if (userId) {
      fetchUserDetails();
      fetchKycCases();
    }
  }, [userId]);

  const fetchUserDetails = async () => {
    if (!userId) return;

    try {
      const response = await usersService.getUserDetails(userId);
      if (response.data) {
        setUserDetail(response.data);
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  const fetchKycCases = async () => {
    if (!userId) return;

    setLoading(true);
    try {
      const response = await kycService.getUserKycCases(userId);
      if (response.data) {
        setKycData(response.data);
      }
    } catch (error) {
      message.error('Failed to fetch KYC cases');
      console.error('Error fetching KYC cases:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetActive = async (caseId: string) => {
    if (!userId) return;

    setActionLoading(true);
    try {
      await kycService.setActiveKycCase(userId, caseId);
      message.success('Active KYC case updated successfully');
      fetchKycCases();
      fetchUserDetails();
    } catch (error) {
      message.error('Failed to set active KYC case');
      console.error('Error setting active KYC case:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDetachKyc = async () => {
    if (!userId) return;

    setActionLoading(true);
    try {
      await usersService.detachKyc(userId);
      message.success('KYC detached successfully');
      fetchKycCases();
      fetchUserDetails();
    } catch (error) {
      message.error('Failed to detach KYC');
      console.error('Error detaching KYC:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleFetchPostIdentData = async (caseId: string) => {
    setFetchingPostIdentCases(prev => new Set(prev).add(caseId));
    try {
      const response = await kycService.fetchPostIdentData(caseId);
      if (response.data) {
        setPostIdentData(response.data);
        setPostIdentModalVisible(true);
      }
    } catch (error) {
      message.error('Failed to fetch PostIdent data');
      console.error('Error fetching PostIdent data:', error);
    } finally {
      setFetchingPostIdentCases(prev => {
        const newSet = new Set(prev);
        newSet.delete(caseId);
        return newSet;
      });
    }
  };

  const handleForceExtract = async (caseId: string) => {
    setExtractingCases(prev => new Set(prev).add(caseId));
    try {
      const response = await kycService.forceExtractKycData(caseId);
      if (response.data?.hasData) {
        message.success('KYC data extracted successfully');
        fetchKycCases(); // Refresh the list
      } else {
        message.warning('Extraction completed but no identity data available from PostIdent');
      }
    } catch (error) {
      message.error('Failed to extract KYC data');
      console.error('Error extracting KYC data:', error);
    } finally {
      setExtractingCases(prev => {
        const newSet = new Set(prev);
        newSet.delete(caseId);
        return newSet;
      });
    }
  };

  const handleRunMrzExtraction = async (caseId: string) => {
    setMrzExtractingCases(prev => new Set(prev).add(caseId));
    try {
      const response = await kycService.runMrzExtraction(caseId);
      if (response.data?.trace.success) {
        const enriched = response.data.enrichedFields;
        message.success(
          enriched.length > 0
            ? `MRZ extraction successful. Enriched fields: ${enriched.join(', ')}`
            : 'MRZ extraction successful',
        );
      } else {
        message.warning(`MRZ extraction failed: ${response.data?.trace.errorMessage || 'Unknown error'}`);
      }
      fetchKycCases();
    } catch (error) {
      message.error('Failed to run MRZ extraction');
      console.error('Error running MRZ extraction:', error);
    } finally {
      setMrzExtractingCases(prev => {
        const newSet = new Set(prev);
        newSet.delete(caseId);
        return newSet;
      });
    }
  };

  const handleDownloadPdf = async (caseId: string, postIdentCaseId: string | null) => {
    try {
      const blob = await kycService.downloadResultPdf(caseId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kyc_result_${postIdentCaseId || caseId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      message.success('PDF downloaded successfully');
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to download PDF');
      console.error('Error downloading PDF:', error);
    }
  };

  const getKycStatusTag = (status: string) => {
    const statusConfig: Record<string, { color: string; icon: React.ReactNode }> = {
      [KYCStatus.NEW]: { color: 'default', icon: null },
      [KYCStatus.REQUIRED]: { color: 'orange', icon: <ExclamationCircleOutlined /> },
      [KYCStatus.IN_PROGRESS]: { color: 'blue', icon: null },
      [KYCStatus.CLOSED]: { color: 'green', icon: <CheckCircleOutlined /> },
    };

    const config = statusConfig[status] || { color: 'default', icon: null };
    return (
      <Tag color={config.color} icon={config.icon}>
        {status}
      </Tag>
    );
  };

  const getKycResultTag = (result: string) => {
    const resultConfig: Record<string, { color: string; icon: React.ReactNode }> = {
      [KYCResult.PENDING]: { color: 'orange', icon: null },
      [KYCResult.SUCCESS]: { color: 'green', icon: <CheckCircleOutlined /> },
      [KYCResult.DECLINED]: { color: 'red', icon: <CloseCircleOutlined /> },
    };

    const config = resultConfig[result] || { color: 'default', icon: null };
    return (
      <Tag color={config.color} icon={config.icon}>
        {result}
      </Tag>
    );
  };

  const getPostIdentCaseId = (providerPayload: Record<string, unknown>): string | null => {
    return (providerPayload?.postIdentCaseId as string) || null;
  };

  const getCaseUrl = (providerPayload: Record<string, unknown>): string | null => {
    const webStart = providerPayload?.webStart as Record<string, unknown>;
    return (webStart?.caseURL as string) || null;
  };

  const formatJsonData = (data: Record<string, unknown> | undefined): string => {
    if (!data) return 'No data available';
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return 'Unable to format data';
    }
  };

  const isAdmin = staff?.role === 'ADMIN';

  const columns = [
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (record: KYCCaseListItem) => (
        <Space direction="vertical" size="small">
          {getKycStatusTag(record.kycStatus)}
          {getKycResultTag(record.kycResult)}
        </Space>
      ),
    },
    {
      title: 'PostIdent Case ID',
      key: 'postIdentCaseId',
      width: 180,
      render: (record: KYCCaseListItem) => {
        const caseId = getPostIdentCaseId(record.providerPayload);
        return caseId ? (
          <Text copyable={{ text: caseId }} style={{ fontSize: '12px' }}>
            {caseId}
          </Text>
        ) : (
          <Text type="secondary">N/A</Text>
        );
      },
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (date: Date) => formatDateTime(date),
      sorter: (a: KYCCaseListItem, b: KYCCaseListItem) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: 'Verlauf',
      key: 'lifecycle',
      width: 230,
      render: (record: KYCCaseListItem) => (
        <div className="table-cell-stack">
          <Text className="table-cell-muted">Aktualisiert: {formatDateTime(record.updatedAt)}</Text>
          <Text className="table-cell-muted">
            Gestartet: {record.startedAt ? formatDateTime(record.startedAt) : '—'}
          </Text>
          <Text className="table-cell-muted">Beendet: {record.closedAt ? formatDateTime(record.closedAt) : '—'}</Text>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right' as const,
      width: 200,
      render: (record: KYCCaseListItem) => {
        const caseUrl = getCaseUrl(record.providerPayload);
        const postIdentCaseId = getPostIdentCaseId(record.providerPayload);
        return (
          <Space size="small" wrap>
            {caseUrl && (
              <Button type="link" icon={<LinkOutlined />} href={caseUrl} target="_blank" size="small">
                Open
              </Button>
            )}
            <Button
              type="link"
              icon={<DownloadOutlined />}
              size="small"
              onClick={() => handleDownloadPdf(record.id, postIdentCaseId)}
            >
              PDF
            </Button>
            {isAdmin && !record.isActive && (
              <Popconfirm
                title="Set as active KYC case?"
                description="This will change the user's active KYC case"
                onConfirm={() => handleSetActive(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <Button type="primary" size="small" loading={actionLoading}>
                  Set Active
                </Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  const expandedRowRender = (record: KYCCaseListItem) => {
    return (
      <div style={{ padding: '16px' }}>
        <Collapse defaultActiveKey={['basic']}>
          <Panel header="Basic Information" key="basic">
            <Descriptions bordered column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }} size="small">
              <Descriptions.Item label="Case ID">{record.id}</Descriptions.Item>
              <Descriptions.Item label="User ID">{record.userId}</Descriptions.Item>
              <Descriptions.Item label="Provider">{record.provider}</Descriptions.Item>
              <Descriptions.Item label="Provider Reference ID">{record.providerReferenceId}</Descriptions.Item>
              <Descriptions.Item label="Status">{getKycStatusTag(record.kycStatus)}</Descriptions.Item>
              <Descriptions.Item label="Result">{getKycResultTag(record.kycResult)}</Descriptions.Item>
              <Descriptions.Item label="Requested At">
                {record.requestedAt ? formatDateTime(record.requestedAt) : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Last Status Check">
                {record.lastStatusCheckAt ? formatDateTime(record.lastStatusCheckAt) : 'N/A'}
              </Descriptions.Item>
            </Descriptions>
          </Panel>

          {record.extractedIdentityData && (
            <Panel header="Extracted Identity Data" key="identity">
              <pre style={{ maxHeight: '400px', overflow: 'auto', fontSize: '12px' }}>
                {formatJsonData(record.extractedIdentityData)}
              </pre>
            </Panel>
          )}

          {record.mrzExtractionTrace && (
            <Panel
              header={
                <Space>
                  MRZ Extraction Pipeline
                  <Badge
                    status={record.mrzExtractionTrace.success ? 'success' : 'error'}
                    text={record.mrzExtractionTrace.success ? 'Success' : 'Failed'}
                  />
                </Space>
              }
              key="mrz"
            >
              {record.mrzExtractionTrace.result && (
                <Descriptions
                  bordered
                  column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }}
                  size="small"
                  style={{ marginBottom: 16 }}
                >
                  <Descriptions.Item label="Document Number">
                    <Text strong>{record.mrzExtractionTrace.result.documentNumber}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="MRZ Format">{record.mrzExtractionTrace.result.mrzFormat}</Descriptions.Item>
                  <Descriptions.Item label="First Name">{record.mrzExtractionTrace.result.firstName}</Descriptions.Item>
                  <Descriptions.Item label="Last Name">{record.mrzExtractionTrace.result.lastName}</Descriptions.Item>
                  <Descriptions.Item label="Date of Birth">
                    {record.mrzExtractionTrace.result.dateOfBirth}
                  </Descriptions.Item>
                  <Descriptions.Item label="Date of Expiry">
                    {record.mrzExtractionTrace.result.dateOfExpiry}
                  </Descriptions.Item>
                  <Descriptions.Item label="Date of Issue">
                    {record.mrzExtractionTrace.result.dateOfIssue || 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Nationality">
                    {record.mrzExtractionTrace.result.nationality}
                  </Descriptions.Item>
                  <Descriptions.Item label="Check Digits Valid">
                    <Tag color={record.mrzExtractionTrace.result.checksValid ? 'green' : 'red'}>
                      {record.mrzExtractionTrace.result.checksValid ? 'Valid' : 'Invalid'}
                    </Tag>
                  </Descriptions.Item>
                  {record.mrzExtractionTrace.result.autocorrections.length > 0 && (
                    <Descriptions.Item label="Autocorrections" span="filled">
                      {record.mrzExtractionTrace.result.autocorrections.join(', ')}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Raw MRZ Lines" span="filled">
                    <pre style={{ margin: 0, fontSize: '11px', fontFamily: 'monospace' }}>
                      {record.mrzExtractionTrace.result.rawLines.join('\n')}
                    </pre>
                  </Descriptions.Item>
                </Descriptions>
              )}

              {record.mrzExtractionTrace.errorMessage && (
                <Alert
                  type="error"
                  message="Pipeline Error"
                  description={record.mrzExtractionTrace.errorMessage}
                  style={{ marginBottom: 16 }}
                />
              )}

              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Pipeline Steps
              </Text>
              <Timeline>
                {record.mrzExtractionTrace.steps.map((step, i) => (
                  <Timeline.Item
                    key={i}
                    color={
                      step.status === MrzPipelineStepStatus.SUCCESS
                        ? 'green'
                        : step.status === MrzPipelineStepStatus.FAILED
                          ? 'red'
                          : 'gray'
                    }
                  >
                    <div>
                      <Text strong>{step.name}</Text>
                      <Text type="secondary" style={{ marginLeft: 8 }}>
                        {step.durationMs}ms
                      </Text>
                    </div>
                    {step.errorMessage && <Text type="danger">{step.errorMessage}</Text>}
                    {step.textOutput && (
                      <pre
                        style={{
                          margin: '4px 0',
                          fontSize: '11px',
                          maxHeight: 120,
                          overflow: 'auto',
                          background: '#f5f5f5',
                          padding: 8,
                        }}
                      >
                        {step.textOutput}
                      </pre>
                    )}
                  </Timeline.Item>
                ))}
              </Timeline>
            </Panel>
          )}

          {record.llmVerificationResult && (
            <Panel
              header={
                <Space>
                  LLM Verification
                  <Badge
                    status={record.llmVerificationResult.status === LlmVerificationStatus.SUCCESS ? 'success' : 'error'}
                    text={record.llmVerificationResult.status === LlmVerificationStatus.SUCCESS ? 'Success' : 'Failed'}
                  />
                </Space>
              }
              key="llm"
            >
              <Descriptions
                bordered
                column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }}
                size="small"
                style={{ marginBottom: 16 }}
              >
                <Descriptions.Item label="Model">{record.llmVerificationResult.modelId}</Descriptions.Item>
                <Descriptions.Item label="Duration">{record.llmVerificationResult.durationMs}ms</Descriptions.Item>
                <Descriptions.Item label="Tried At">
                  {formatDateTime(record.llmVerificationResult.triedAt)}
                </Descriptions.Item>
                {record.llmVerificationResult.tokenUsage && (
                  <Descriptions.Item label="Tokens">
                    in {record.llmVerificationResult.tokenUsage.input} / out{' '}
                    {record.llmVerificationResult.tokenUsage.output}
                  </Descriptions.Item>
                )}
              </Descriptions>

              {record.llmVerificationResult.errorMessage && (
                <Alert
                  type="error"
                  message="LLM Error"
                  description={record.llmVerificationResult.errorMessage}
                  style={{ marginBottom: 16 }}
                />
              )}

              {record.llmVerificationResult.fields && (
                <Descriptions title="Extracted Fields" bordered column={1} size="small" style={{ marginBottom: 16 }}>
                  {Object.entries(record.llmVerificationResult.fields).map(([name, field]) => {
                    const mismatched = record.llmVerificationResult?.mismatchesWithMrz?.includes(name);
                    return (
                      <Descriptions.Item
                        key={name}
                        label={
                          <Space>
                            {name}
                            {mismatched && <Tag color="orange">MRZ mismatch</Tag>}
                          </Space>
                        }
                      >
                        <Text strong>{field.value ?? 'N/A'}</Text>
                        <div style={{ fontSize: '12px', color: '#888', marginTop: 4 }}>{field.reasoning}</div>
                      </Descriptions.Item>
                    );
                  })}
                </Descriptions>
              )}

              {record.llmVerificationResult.overallReasoning && (
                <Alert
                  type="info"
                  message="Overall Reasoning"
                  description={record.llmVerificationResult.overallReasoning}
                />
              )}
            </Panel>
          )}

          <Panel header="Provider Payload" key="payload">
            <pre style={{ maxHeight: '400px', overflow: 'auto', fontSize: '12px' }}>
              {formatJsonData(record.providerPayload)}
            </pre>
          </Panel>

          {getPostIdentCaseId(record.providerPayload) && (
            <Panel header="PostIdent Data" key="postident">
              <div style={{ marginBottom: 16 }}>
                <Space>
                  <Button
                    type="primary"
                    icon={<CloudDownloadOutlined />}
                    onClick={() => handleFetchPostIdentData(record.id)}
                    loading={fetchingPostIdentCases.has(record.id)}
                  >
                    Fetch PostIdent Data
                  </Button>
                  <Button
                    type="default"
                    icon={<SyncOutlined />}
                    onClick={() => handleForceExtract(record.id)}
                    loading={extractingCases.has(record.id)}
                    disabled={!!record.extractedIdentityData && !!record.resultPdfFileId}
                  >
                    {record.extractedIdentityData && record.resultPdfFileId
                      ? 'Data Already Extracted'
                      : record.extractedIdentityData && !record.resultPdfFileId
                        ? 'Re-extract (Missing PDF)'
                        : 'Force Extract Data'}
                  </Button>
                  {record.resultPdfFileId && (
                    <Button
                      type="default"
                      icon={<ScanOutlined />}
                      onClick={() => handleRunMrzExtraction(record.id)}
                      loading={mrzExtractingCases.has(record.id)}
                    >
                      Run MRZ Extraction
                    </Button>
                  )}
                </Space>
              </div>
              <Text type="secondary">
                {record.extractedIdentityData && record.resultPdfFileId
                  ? 'Identity data and PDF already extracted. Use "Fetch PostIdent Data" to view raw data from PostIdent.'
                  : record.extractedIdentityData && !record.resultPdfFileId
                    ? 'Identity data extracted but PDF is missing. Click "Re-extract" to fetch the PDF.'
                    : 'Click "Force Extract Data" to fetch and store identity data from PostIdent.'}
              </Text>
            </Panel>
          )}
        </Collapse>
      </div>
    );
  };

  return (
    <>
      <div className="page-toolbar">
        <Space>
          <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate('/users')}>
            Kunden
          </Button>
          <Button type="text" onClick={() => window.close()}>
            Fenster schließen
          </Button>
        </Space>

        {userDetail && (
          <Space align="center">
            <Avatar size="large" icon={<UserOutlined />} style={{ backgroundColor: token.colorPrimary }} />
            <div style={{ lineHeight: 1.5 }}>
              <Text strong style={{ fontSize: '14px' }}>
                {userDetail.user.firstName} {userDetail.user.lastName}
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {userDetail.user.phoneNumber} | {userDetail.user.email || 'No email'}
              </Text>
            </div>
          </Space>
        )}
      </div>

      <>
        <Card bodyStyle={{ padding: 0 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
            </div>
          ) : kycData ? (
            <>
              {kycData.activeCase && (
                <Alert
                  message="Active KYC Case"
                  description={`Case ID: ${
                    getPostIdentCaseId(kycData.activeCase.providerPayload) || kycData.activeCase.id
                  } - Status: ${kycData.activeCase.kycStatus} - Result: ${kycData.activeCase.kycResult}`}
                  type="info"
                  showIcon
                  style={{ margin: 16 }}
                  action={
                    userDetail?.user.activeKycCaseId && (
                      <Popconfirm
                        title="Are you sure you want to detach KYC from this user?"
                        description="This will remove the KYC association but won't delete the KYC case itself."
                        onConfirm={handleDetachKyc}
                        okText="Yes"
                        cancelText="No"
                        okButtonProps={{ danger: true }}
                      >
                        <Button danger icon={<DisconnectOutlined />} loading={actionLoading} size="small">
                          Detach KYC
                        </Button>
                      </Popconfirm>
                    )
                  }
                />
              )}
              <Table
                columns={columns}
                dataSource={kycData.cases}
                rowKey="id"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showTotal: total => `Total ${total} cases`,
                }}
                scroll={{ x: 940 }}
                expandable={{
                  expandedRowRender,
                  rowExpandable: () => true,
                }}
                rowClassName={record => (record.isActive ? 'active-row' : '')}
              />
              <style>
                {`
                  .active-row {
                    background-color: #f0f8ff !important;
                  }
                  .active-row:hover > td {
                    background-color: #e6f7ff !important;
                  }
                `}
              </style>
              <div style={{ padding: 16, textAlign: 'right', borderTop: '1px solid #f0f0f0' }}>
                <Text type="secondary">Total KYC Cases: {kycData.total}</Text>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p>No KYC cases available</p>
            </div>
          )}
        </Card>

        {/* PostIdent Data Modal */}
        <Modal
          title="PostIdent Raw Data"
          open={postIdentModalVisible}
          onCancel={() => setPostIdentModalVisible(false)}
          width={900}
          footer={[
            <Button key="close" onClick={() => setPostIdentModalVisible(false)}>
              Close
            </Button>,
          ]}
        >
          {postIdentData && (
            <div>
              <Descriptions
                bordered
                column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }}
                size="small"
                style={{ marginBottom: 16 }}
              >
                <Descriptions.Item label="Case ID">{postIdentData.caseId}</Descriptions.Item>
                <Descriptions.Item label="PostIdent Case ID">{postIdentData.postIdentCaseId}</Descriptions.Item>
                <Descriptions.Item label="Status">{postIdentData.status || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Result">{postIdentData.result || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Has Documents">{postIdentData.hasDocuments ? 'Yes' : 'No'}</Descriptions.Item>
                <Descriptions.Item label="Fetched At">
                  {new Date(postIdentData.fetchedAt).toLocaleString()}
                </Descriptions.Item>
              </Descriptions>
              <Typography.Title level={5}>Raw Data from PostIdent:</Typography.Title>
              <div
                style={{
                  maxHeight: '500px',
                  overflow: 'auto',
                  backgroundColor: '#f5f5f5',
                  padding: '12px',
                  borderRadius: '4px',
                  border: '1px solid #d9d9d9',
                }}
              >
                <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(postIdentData.rawData, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </Modal>
      </>
    </>
  );
}
